const AUTH_REQUIRED_MESSAGE = "Sign in to use transcription and prompt features.";
const MIC_PERMISSION_MESSAGE = "Please allow microphone access to start recording.";
const MIC_NOT_FOUND_MESSAGE = "No microphone was found. Connect a microphone and try again.";
const SECURE_CONTEXT_MESSAGE = "Microphone access requires HTTPS or localhost.";
const NETWORK_MESSAGE = "Connection lost. Check your internet and try again.";
const TRANSCRIPTION_MESSAGE =
  "Transcription is temporarily unavailable. Please try again shortly.";
const PROMPT_MESSAGE =
  "Prompt processing is temporarily unavailable. Please try again shortly.";
export const USAGE_LIMIT_ERROR_CODE = "USAGE_LIMIT_EXCEEDED";
export const USAGE_LIMIT_MESSAGE =
  "You've used all your free transcription minutes this week. Upgrade to ZT Pro to keep going.";

const USAGE_LIMIT_SERVER_CODES = new Set([
  "free_plan_minutes_exhausted",
  "subscription_error",
  USAGE_LIMIT_ERROR_CODE,
]);
const USAGE_LIMIT_MESSAGE_PATTERN =
  /free plan minutes|minutes are exhausted|minutes exhausted|upgrade to pro plan/i;
const USAGE_LIMIT_CLOSE_REASON_PATTERN =
  /free plan minutes|minutes exhausted|usage limit/i;

const readStatus = (value) => value?.status || value?.response?.status || null;
const readErrorCode = (value) =>
  value?.errorCode ||
  value?.code ||
  value?.error?.errorCode ||
  value?.error?.code ||
  value?.response?.data?.errorCode ||
  value?.response?.data?.code ||
  null;
const readErrorType = (value) =>
  value?.error?.type || value?.type || value?.response?.data?.type || null;
const readName = (value) => value?.name || null;
const readMessage = (value) =>
  String(
    value?.message ||
    value?.error?.message ||
    value?.response?.data?.error ||
    value?.response?.data?.message ||
    ""
  ).trim();

const isOffline = () =>
  typeof navigator !== "undefined" &&
  Object.prototype.hasOwnProperty.call(navigator, "onLine") &&
  navigator.onLine === false;

const matches = (message, pattern) => pattern.test(String(message || ""));

export const isUsageLimitSignal = (value) => {
  if (!value || typeof value !== "object") return false;

  const closeCode = Number(value.closeCode ?? value.code);
  const closeReason = value.closeReason ?? value.reason;
  if (closeCode === 1008 && matches(closeReason, USAGE_LIMIT_CLOSE_REASON_PATTERN)) {
    return true;
  }

  const errorCode = readErrorCode(value);
  if (errorCode && USAGE_LIMIT_SERVER_CODES.has(String(errorCode))) return true;

  const errorType = readErrorType(value);
  if (errorType && USAGE_LIMIT_SERVER_CODES.has(String(errorType))) return true;

  const message = readMessage(value);
  if (matches(message, USAGE_LIMIT_MESSAGE_PATTERN)) return true;

  return false;
};

export const createUsageLimitError = (payload) => {
  const message =
    (payload && (payload.message || payload.error?.message)) || USAGE_LIMIT_MESSAGE;
  const err = new Error(message);
  err.errorCode = USAGE_LIMIT_ERROR_CODE;
  err.details = payload || null;
  return err;
};

// One predicate per failure class, shared by formatAppError (what the user
// sees) and classifyAppError (what analytics records as `error_type`) so the
// two can never disagree.
const isAuthError = ({ status, errorCode, message }) =>
  status === 401 ||
  status === 403 ||
  errorCode === "AUTH_REQUIRED" ||
  matches(message, /no auth cookies|sign in to continue|unable to fetch websocket token/i);

const isMicPermissionError = ({ errorCode, name, message }) =>
  errorCode === "MIC_PERMISSION_DENIED" ||
  name === "NotAllowedError" ||
  matches(message, /microphone.*denied|permission.*microphone|allow microphone/i);

const isMicNotFoundError = ({ errorCode, name, message }) =>
  errorCode === "MIC_NOT_FOUND" ||
  name === "NotFoundError" ||
  matches(message, /no microphone|microphone.*not found/i);

const isSecureContextError = ({ errorCode, message }) =>
  errorCode === "SECURE_CONTEXT_REQUIRED" || matches(message, /https|secure context/i);

const isNetworkError = ({ errorCode, message }) =>
  isOffline() ||
  errorCode === "NETWORK_ERROR" ||
  matches(message, /network issue|connection error|websocket connection error|ws open timeout|timeout/i);

const isPromptError = ({ errorCode, message }, feature) =>
  errorCode === "PROMPT_UNAVAILABLE" || (feature === "prompt" && matches(message, /prompt/i));

const isTranscriptionError = ({ errorCode, message }, feature) =>
  errorCode === "TRANSCRIPTION_UNAVAILABLE" ||
  feature === "transcription" ||
  matches(message, /transcription|audio processing|realtime/i);

const readFacts = (value) => ({
  status: readStatus(value),
  errorCode: readErrorCode(value),
  name: readName(value),
  message: readMessage(value),
});

/**
 * Stable, low-cardinality failure class for analytics (`error_type`). Shared
 * vocabulary with the native apps — add values here, never inline strings.
 */
export const APP_ERROR_TYPES = {
  usageLimit: "usage_limit",
  authRequired: "auth_required",
  micPermissionDenied: "mic_permission_denied",
  micNotFound: "mic_not_found",
  secureContextRequired: "secure_context_required",
  network: "network",
  rateLimited: "rate_limited",
  serverError: "server_error",
  requestError: "request_error",
  promptUnavailable: "prompt_unavailable",
  transcriptionUnavailable: "transcription_unavailable",
  unknown: "unknown",
};

// The hooks hand their onError callbacks an already-formatted message rather
// than the raw error, so classification must also work backwards from text.
const TYPE_BY_MESSAGE = {
  [USAGE_LIMIT_MESSAGE]: APP_ERROR_TYPES.usageLimit,
  [AUTH_REQUIRED_MESSAGE]: APP_ERROR_TYPES.authRequired,
  [MIC_PERMISSION_MESSAGE]: APP_ERROR_TYPES.micPermissionDenied,
  [MIC_NOT_FOUND_MESSAGE]: APP_ERROR_TYPES.micNotFound,
  [SECURE_CONTEXT_MESSAGE]: APP_ERROR_TYPES.secureContextRequired,
  [NETWORK_MESSAGE]: APP_ERROR_TYPES.network,
  [PROMPT_MESSAGE]: APP_ERROR_TYPES.promptUnavailable,
  [TRANSCRIPTION_MESSAGE]: APP_ERROR_TYPES.transcriptionUnavailable,
};

export const classifyAppError = (value, { feature = "transcription" } = {}) => {
  if (typeof value === "string") {
    return TYPE_BY_MESSAGE[value.trim()] || APP_ERROR_TYPES.unknown;
  }
  if (!value) return APP_ERROR_TYPES.unknown;

  const facts = readFacts(value);
  if (isUsageLimitSignal(value)) return APP_ERROR_TYPES.usageLimit;
  if (isAuthError(facts)) return APP_ERROR_TYPES.authRequired;
  if (isMicPermissionError(facts)) return APP_ERROR_TYPES.micPermissionDenied;
  if (isMicNotFoundError(facts)) return APP_ERROR_TYPES.micNotFound;
  if (isSecureContextError(facts)) return APP_ERROR_TYPES.secureContextRequired;
  if (isNetworkError(facts)) return APP_ERROR_TYPES.network;
  // HTTP status carries more signal than the generic feature fallback below.
  if (facts.status === 429) return APP_ERROR_TYPES.rateLimited;
  if (facts.status >= 500) return APP_ERROR_TYPES.serverError;
  if (facts.status >= 400) return APP_ERROR_TYPES.requestError;
  if (isPromptError(facts, feature)) return APP_ERROR_TYPES.promptUnavailable;
  if (isTranscriptionError(facts, feature)) return APP_ERROR_TYPES.transcriptionUnavailable;
  return APP_ERROR_TYPES.unknown;
};

export const formatAppError = (value, { feature = "transcription" } = {}) => {
  const facts = readFacts(value);

  if (isUsageLimitSignal(value)) return USAGE_LIMIT_MESSAGE;
  if (isAuthError(facts)) return AUTH_REQUIRED_MESSAGE;
  if (isMicPermissionError(facts)) return MIC_PERMISSION_MESSAGE;
  if (isMicNotFoundError(facts)) return MIC_NOT_FOUND_MESSAGE;
  if (isSecureContextError(facts)) return SECURE_CONTEXT_MESSAGE;
  if (isNetworkError(facts)) return NETWORK_MESSAGE;
  if (isPromptError(facts, feature)) return PROMPT_MESSAGE;
  if (isTranscriptionError(facts, feature)) return TRANSCRIPTION_MESSAGE;

  return facts.message || "Something went wrong. Please try again.";
};

export const createSecureContextError = () => {
  const error = new Error("Mic requires HTTPS (or localhost) secure context.");
  error.errorCode = "SECURE_CONTEXT_REQUIRED";
  return error;
};
